import { gql, MutationResult, useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { succesToast, warningToast } from 'basics/utils/toast';
import { Speaker, SpeakerInput } from 'modules/events/basics/types/speaker.types';
import { speakerFragment } from 'modules/events/graphql/fragments/speaker.fragments';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const createOrUpdateSpeakerMutationCall = gql`
mutation createOrUpdateSpeakerMutation($speaker: SpeakerInput!) {
    createOrUpdateSpeaker(speaker: $speaker) {
        ${speakerFragment}
    }
}
`;

type ResultType = {
  createOrUpdateSpeaker: Speaker;
};

type UseCreateOrUpdateSpeakerMutation = [
  (speakerProfile: SpeakerInput) => Promise<Speaker | undefined | null>,
  MutationResult<ResultType>,
];

export const useCreateOrUpdateSpeakerMutation = (): UseCreateOrUpdateSpeakerMutation => {
  const { t } = useTranslation();
  const [createOrUpdateSpeaker, state] = useMutation<ResultType>(createOrUpdateSpeakerMutationCall);

  const executeMutation = useCallback(async (speakerProfile: SpeakerInput) => {
    try {
      const data = await createOrUpdateSpeaker({
        variables: { speaker: speakerProfile },
      });
      succesToast(t('createSpeaker_toast_success'));
      return data.data?.createOrUpdateSpeaker;
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error, speakerProfile);
        Logger.logError({
          error,
          customContext,
          transactionName: 'createOrUpdateSpeaker.ts - useCreateOrUpdateSpeakerMutation',
        });
        if (customContext.description) warningToast(String(customContext.description));
      }
      return null;
    }
  }, [createOrUpdateSpeaker, t]);

  return [executeMutation, state];
};
